import { useEffect, useRef, useState } from "react";
import { toast } from "@volca/ui/lib/toast";
import { useApi } from "@/core/api";
import { downloadReport } from "./query";
import type { ProfitabilityRun } from "./types";
import { downloadFilename, reportError } from "./utils";
/**
 * Owns the XLSX download for one report. The request can take several polls
 * while the API rebuilds a missing export, so it is cancelled when the view
 * closes and reports failure as a toast rather than parking an error in the
 * header.
 */
export const useReportDownload = (
  businessId: string,
  /** Null while the page has no run; `download` then does nothing. */
  run: ProfitabilityRun | null,
) => {
  const api = useApi();
  const [downloading, setDownloading] = useState(false);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);

  const download = async () => {
    if (controller.current || !run) return;
    const request = new AbortController();
    controller.current = request;
    setDownloading(true);
    try {
      const blob = await downloadReport(
        api,
        businessId,
        run.id,
        request.signal,
      );
      request.signal.throwIfAborted();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadFilename(run);
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      if (!request.signal.aborted)
        toast.error("The report could not be downloaded", {
          description: reportError(error),
        });
    } finally {
      controller.current = null;
      if (!request.signal.aborted) setDownloading(false);
    }
  };

  return { download: () => void download(), downloading };
};
